import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CardActions,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { Link, useParams } from "react-router-dom";
import { API_ENDPOINT, ONSITE_MODULE_ITEMS } from "../../utils/constants";
import MuPb from "../../widgets/MuPb";
import OtpInput from "./OtpInput";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { toast } from "react-toastify";
import { toastError } from "../../utils/util";
import {
  deleteOnsite,
  deleteOtpVerify,
  getOtpVerify,
  isOnsiteActivated,
  saveOnsite,
  saveOtpVerify,
} from "./utils";
import OnsiteReuseContainer from "./OnsiteReuseContainer";
import {
  BarChart3,
  IdCard,
  Layers3,
  Link2Off,
  MapPin,
  Printer,
  ScanLine,
  Search,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  TriangleAlert,
  UserPlus,
  Users,
} from "lucide-react";

const MODULE_META = {
  [ONSITE_MODULE_ITEMS.ONSITE_REGISTRATION]: {
    label: "Registration",
    Icon: UserPlus,
  },
  [ONSITE_MODULE_ITEMS.SECURITY_SCANNING]: {
    label: "Access Control",
    Icon: ShieldCheck,
  },
  [ONSITE_MODULE_ITEMS.KIOSK_BADGE_PRINTING]: {
    label: "Self Service",
    Icon: Sparkles,
  },
  [ONSITE_MODULE_ITEMS.BULK_PRINTING]: {
    label: "Batch Printing",
    Icon: Layers3,
  },
  [ONSITE_MODULE_ITEMS.FOOTFALL_ANALYSIS]: {
    label: "Live Analytics",
    Icon: BarChart3,
  },
  [ONSITE_MODULE_ITEMS.SEARCH_AND_PRINT]: {
    label: "Quick Search",
    Icon: Search,
  },
  [ONSITE_MODULE_ITEMS.BULK_STUDENT_BADGES]: {
    label: "Student Badges",
    Icon: Users,
  },
  [ONSITE_MODULE_ITEMS.SERVICE_BADGE]: {
    label: "Service Badge",
    Icon: IdCard,
  },
  [ONSITE_MODULE_ITEMS.SERVICE_BADGE_SCAN]: {
    label: "Badge Scanning",
    Icon: ScanLine,
  },
  [ONSITE_MODULE_ITEMS.LOCATIONWISE_SECURITY_SCAN]: {
    label: "Location Scan",
    Icon: MapPin,
  },
  [ONSITE_MODULE_ITEMS.PRINT_STAFF_BADGE]: {
    label: "Staff Badges",
    Icon: Printer,
  },
};

const getColorTint = (color, opacity = 0.1) => {
  const hex = color?.replace("#", "");

  if (!hex || !/^[0-9a-fA-F]{6}$/.test(hex)) {
    return `rgba(124, 58, 237, ${opacity})`;
  }

  const value = parseInt(hex, 16);
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;

  return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
};

const SharableOnsite = () => {
  const { id } = useParams();

  const [open, setOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const verificationInFlight = useRef(false);
  const [otp, setOtp] = useState("");
  const [currentGroup, setCurrentGroup] = useState();
  const [requesting, setRequesting] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_ENDPOINT}/onsite/get-modules`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${id}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error("LINK_EXPIRED");
          }

          throw new Error("LOAD_FAILED");
        }

        const data = await response.json();

        setModules(data);
      } catch (err) {
        setError(err.message || "LOAD_FAILED");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchModules();
    }
  }, [id]);

  if (loading) {
    return <MuPb />;
  }

  if (error) {
    const isExpired = error === "LINK_EXPIRED";

    return (
      <OnsiteReuseContainer maxWidth="sm">
        <Box
          sx={{
            minHeight: "70vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 540,
              textAlign: "center",
              backgroundColor: "background.paper",
              borderRadius: 4,
              px: {
                xs: 3,
                sm: 5,
              },
              py: {
                xs: 4,
                sm: 5,
              },
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 14px 45px rgba(0,0,0,0.08)",
            }}
          >
            <Box
              sx={{
                width: 82,
                height: 82,
                borderRadius: "50%",
                backgroundColor: isExpired ? "#fff1f1" : "#fff7e6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              {isExpired ? (
                <Link2Off size={40} strokeWidth={1.8} color="#d32f2f" />
              ) : (
                <TriangleAlert size={40} strokeWidth={1.8} color="#ed6c02" />
              )}
            </Box>

            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                mb: 1.5,
                fontSize: {
                  xs: "1.7rem",
                  sm: "2rem",
                },
              }}
            >
              {isExpired ? "Access Link Expired" : "Unable to Load Modules"}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                maxWidth: 430,
                mx: "auto",
                lineHeight: 1.7,
                fontSize: "1rem",
              }}
            >
              {isExpired
                ? "This onsite access link is no longer valid or has expired."
                : "We couldn't load the onsite modules at the moment. Please try again later."}
            </Typography>

            {isExpired && (
              <>
                <Typography
                  variant="body1"
                  sx={{
                    mt: 1.5,
                    maxWidth: 430,
                    mx: "auto",
                    lineHeight: 1.7,
                    fontWeight: 600,
                  }}
                >
                  Please contact the IMTMA Tech Admin/Team to request a new
                  access link.
                </Typography>

                <Box
                  sx={{
                    mt: 4,
                    px: 2,
                    py: 1.4,
                    borderRadius: 2.5,
                    backgroundColor: "action.hover",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    maxWidth: "100%",
                  }}
                >
                  <ShieldAlert size={18} strokeWidth={1.8} />

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      textAlign: "left",
                    }}
                  >
                    Expired access links cannot be reused.
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </OnsiteReuseContainer>
    );
  }

  const handleClose = () => {
    if (verificationInFlight.current) return;

    setOtp("");
    setOpen(false);
  };

  const onOtpEntered = (otp) => {
    setOtp(otp);
  };

  const deactivateModule = (group) => {
    deleteOnsite(group.key);
    document.location.reload();
  };

  const requestOtp = async (cd) => {
    try {
      setRequesting(true);
      setCurrentGroup(cd);

      const { data } = await axios.get(
        `${API_ENDPOINT}/onsite/request-activation-code?id=${cd._id}`,
        {
          headers: {
            "content-type": "application/json",
            Authorization: `Bearer ${id}`,
          },
        }
      );

      toast.success(data.message);

      saveOtpVerify(data.token);

      onActivate(cd);
      setOpen(true);
    } catch (error) {
      toastError(error);
    } finally {
      setRequesting(false);
    }
  };

  const onActivate = (cd) => {
    setOtp("");
    setCurrentGroup(cd);
    setOpen(true);
  };

  const onSubmitOtp = async (code = otp) => {
    if (!/^[0-9]{4}$/.test(code) || verificationInFlight.current) return;

    verificationInFlight.current = true;

    try {
      setVerifying(true);

      const { data } = await axios.get(
        `${API_ENDPOINT}/onsite/verify-activation-code?otp=${code}&code=${getOtpVerify()}`,
        {
          headers: {
            "content-type": "application/json",
            Authorization: `Bearer ${id}`,
          },
        }
      );

      toast.success(data.message);

      saveOnsite(data.key, data.token);

      setOpen(false);

      deleteOtpVerify();

      setTimeout(() => {
        document.location.reload();
      }, 300);
    } catch (error) {
      toastError(error);
    } finally {
      verificationInFlight.current = false;
      setVerifying(false);
    }
  };

  const handleCopyLink = (link) => {
    const fullLink = `${window.location.origin}${link}`;

    navigator.clipboard.writeText(fullLink);

    toast.success("Module link copied");
  };

  const activeCount = modules.filter((group) =>
    isOnsiteActivated(group.key)
  ).length;
  const inactiveCount = modules.length - activeCount;
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredModules = modules.filter((group) => {
    const isActive = isOnsiteActivated(group.key);
    const matchesFilter =
      activeFilter === "all" ||
      (activeFilter === "active" && isActive) ||
      (activeFilter === "inactive" && !isActive);
    const matchesSearch =
      !normalizedSearch ||
      group.title?.toLowerCase().includes(normalizedSearch) ||
      group.description?.toLowerCase().includes(normalizedSearch);

    return matchesFilter && matchesSearch;
  });

  return (
    <OnsiteReuseContainer maxWidth="lg">
      <Box sx={{ pt: { xs: 2, sm: 3 }, pb: 4 }}>
        <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4 } }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.55,
              mb: 1.5,
              borderRadius: 10,
              bgcolor: "#ede9fe",
              color: "#6d28d9",
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            <Sparkles size={15} />
            Onsite Operations Hub
          </Box>
          <Typography
            component="h1"
            sx={{
              color: "#0f172a",
              fontSize: { xs: "1.75rem", sm: "2.25rem" },
              lineHeight: 1.2,
              fontWeight: 750,
              letterSpacing: "-0.035em",
            }}
          >
            Live Venue &amp; Kiosk Modules
          </Typography>
          <Typography
            sx={{
              maxWidth: 720,
              mx: "auto",
              mt: 1,
              color: "#64748b",
              fontSize: { xs: "0.9rem", sm: "0.98rem" },
              lineHeight: 1.65,
            }}
          >
            Manage visitor check-ins, badge printing, security checkpoints,
            and live venue analytics from one place.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            p: { xs: 1.5, sm: 2 },
            mb: 3,
            bgcolor: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 2.5,
            boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ overflowX: "auto", pb: { xs: 0.5, md: 0 } }}
          >
            {[
              { value: "all", label: "All Modules", count: modules.length },
              { value: "active", label: "Active", count: activeCount },
              {
                value: "inactive",
                label: "Inactive",
                count: inactiveCount,
              },
            ].map((filter) => {
              const selected = activeFilter === filter.value;

              return (
                <Button
                  key={filter.value}
                  onClick={() => setActiveFilter(filter.value)}
                  variant={selected ? "contained" : "outlined"}
                  startIcon={
                    filter.value === "active" ? (
                      <CheckCircleIcon />
                    ) : filter.value === "inactive" ? (
                      <LockOutlinedIcon />
                    ) : null
                  }
                  sx={{
                    flexShrink: 0,
                    minHeight: 40,
                    px: 1.75,
                    borderRadius: 1.75,
                    borderColor: selected ? "#7c3aed" : "#d7dee9",
                    bgcolor: selected ? "#7c3aed" : "#fff",
                    color: selected ? "#fff" : "#334155",
                    boxShadow: "none",
                    textTransform: "none",
                    fontSize: "0.84rem",
                    fontWeight: 650,
                    "&:hover": {
                      borderColor: "#7c3aed",
                      bgcolor: selected ? "#6d28d9" : "#faf5ff",
                      boxShadow: "none",
                    },
                  }}
                >
                  {filter.label} ({filter.count})
                </Button>
              );
            })}
          </Stack>

          <TextField
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search modules by name..."
            size="small"
            inputProps={{ "aria-label": "Search onsite modules" }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", md: 310 },
              "& .MuiOutlinedInput-root": {
                height: 42,
                borderRadius: 1.75,
                bgcolor: "#f8fafc",
                fontSize: "0.875rem",
              },
            }}
          />
        </Box>

        {filteredModules.length === 0 ? (
          <Box
            sx={{
              py: 8,
              textAlign: "center",
              bgcolor: "#fff",
              border: "1px dashed #cbd5e1",
              borderRadius: 2.5,
            }}
          >
            <SearchRoundedIcon sx={{ color: "#94a3b8", fontSize: 34 }} />
            <Typography sx={{ mt: 1, color: "#475569", fontWeight: 600 }}>
              No modules found
            </Typography>
            <Typography sx={{ mt: 0.5, color: "#94a3b8", fontSize: "0.84rem" }}>
              Try changing the status filter or search term.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={{ xs: 2, md: 2.5 }} alignItems="stretch">
            {filteredModules.map((group, index) => {
              const isActive = isOnsiteActivated(group.key);
              const accentColor = group.color || "#7c3aed";
              const moduleMeta = MODULE_META[group.key] || {
                label: "Onsite Module",
                Icon: Sparkles,
              };
              const ModuleIcon = moduleMeta.Icon;

              return (
                <Grid item xs={12} sm={6} md={4} key={group._id || index}>
                  <Card
                    elevation={0}
                    sx={{
                      position: "relative",
                      height: "100%",
                      minHeight: 330,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      borderRadius: 2.5,
                      border: `1px solid ${
                        isActive ? getColorTint(accentColor, 0.6) : "#dbe3ef"
                      }`,
                      bgcolor: "#fff",
                      boxShadow: isActive
                        ? `0 10px 28px ${getColorTint(accentColor, 0.12)}`
                        : "0 6px 20px rgba(15, 23, 42, 0.045)",
                      transition: "transform 180ms ease, box-shadow 180ms ease",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: "0 0 auto 0",
                        height: 4,
                        bgcolor: accentColor,
                      },
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: `0 14px 32px ${getColorTint(
                          accentColor,
                          0.14
                        )}`,
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 2.25, sm: 2.5 },
                        pb: 1.5,
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1.5,
                          mb: 2.25,
                        }}
                      >
                        <Box
                          sx={{
                            width: 46,
                            height: 46,
                            flexShrink: 0,
                            display: "grid",
                            placeItems: "center",
                            borderRadius: 2,
                            bgcolor: getColorTint(accentColor, 0.08),
                            color: accentColor,
                            border: `1px solid ${getColorTint(
                              accentColor,
                              0.2
                            )}`,
                          }}
                        >
                          <ModuleIcon size={23} strokeWidth={2} />
                        </Box>
                        <Box
                          sx={{
                            px: 1.1,
                            py: 0.45,
                            borderRadius: 1.25,
                            bgcolor: getColorTint(accentColor, 0.08),
                            color: accentColor,
                            border: `1px solid ${getColorTint(
                              accentColor,
                              0.18
                            )}`,
                            fontSize: "0.7rem",
                            fontWeight: 700,
                            lineHeight: 1.2,
                          }}
                        >
                          {moduleMeta.label}
                        </Box>
                      </Box>

                      <Typography
                        component="h2"
                        sx={{
                          color: "#0f172a",
                          fontSize: "1.05rem",
                          lineHeight: 1.35,
                          fontWeight: 750,
                        }}
                      >
                        {group.title}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 1,
                          color: "#64748b",
                          fontSize: "0.86rem",
                          lineHeight: 1.6,
                        }}
                      >
                        {group.description}
                      </Typography>

                      <Box sx={{ mt: "auto", pt: 2.25 }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.65,
                            px: 1.1,
                            py: 0.55,
                            borderRadius: 1.5,
                            bgcolor: isActive ? "#ecfdf5" : "#f1f5f9",
                            color: isActive ? "#047857" : "#64748b",
                            border: `1px solid ${
                              isActive ? "#a7f3d0" : "#dbe3ef"
                            }`,
                            fontSize: "0.74rem",
                            fontWeight: 650,
                          }}
                        >
                          {isActive ? (
                            <CheckCircleIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <LockOutlinedIcon sx={{ fontSize: 15 }} />
                          )}
                          {isActive ? "Active & Ready" : "Requires Activation"}
                        </Box>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 2, pt: 1, gap: 1 }}>
                      {!isActive ? (
                        <LoadingButton
                          fullWidth
                          loading={
                            currentGroup?._id === group._id && requesting
                          }
                          variant="contained"
                          startIcon={<KeyOutlinedIcon />}
                          onClick={() => requestOtp(group)}
                          sx={{
                            minHeight: 42,
                            borderRadius: 1.75,
                            bgcolor: accentColor,
                            boxShadow: `0 6px 14px ${getColorTint(
                              accentColor,
                              0.2
                            )}`,
                            textTransform: "none",
                            fontSize: "0.86rem",
                            fontWeight: 700,
                            "&:hover": {
                              bgcolor: accentColor,
                              filter: "brightness(0.92)",
                            },
                          }}
                        >
                          Activate Module
                        </LoadingButton>
                      ) : (
                        <>
                          <Button
                            component={Link}
                            to={group.link}
                            variant="contained"
                            endIcon={<OpenInNewRoundedIcon />}
                            sx={{
                              flexGrow: 1,
                              minHeight: 42,
                              borderRadius: 1.75,
                              bgcolor: accentColor,
                              boxShadow: "none",
                              textTransform: "none",
                              fontWeight: 700,
                              "&:hover": {
                                bgcolor: accentColor,
                                filter: "brightness(0.92)",
                                boxShadow: "none",
                              },
                            }}
                          >
                            Open
                          </Button>
                          <IconButton
                            onClick={() => handleCopyLink(group.link)}
                            aria-label={`Copy ${group.title} link`}
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: 1.75,
                              border: "1px solid #dbe3ef",
                              color: "#64748b",
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => deactivateModule(group)}
                            aria-label={`Deactivate ${group.title}`}
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: 1.75,
                              border: "1px solid #fecaca",
                              bgcolor: "#fff7f7",
                              color: "#dc2626",
                              "&:hover": { bgcolor: "#fee2e2" },
                            }}
                          >
                            <HighlightOffIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
          Activate {currentGroup?.title}
        </DialogTitle>

        <DialogContent sx={{ pt: "12px !important" }}>
          <Typography sx={{ mb: 2.5, color: "#64748b", fontSize: "0.88rem" }}>
            Enter the four-digit activation code sent to the administrator.
          </Typography>
          {open && (
            <OtpInput
              onComplete={onOtpEntered}
              onSubmit={onSubmitOtp}
              disabled={verifying}
            />
          )}

          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              mt: 2.5,
              p: 1.25,
              borderRadius: 1.5,
              bgcolor: "#f8fafc",
              border: "1px solid #e2e8f0",
            }}
          >
            Please contact the administrator if you have not received the OTP.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={handleClose}
            disabled={verifying}
            sx={{ color: "#64748b", textTransform: "none" }}
          >
            Cancel
          </Button>

          <LoadingButton
            loading={verifying}
            onClick={() => onSubmitOtp()}
            variant="contained"
            disabled={!/^[0-9]{4}$/.test(otp) || verifying}
            sx={{
              bgcolor: "#7c3aed",
              borderRadius: 1.75,
              boxShadow: "none",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { bgcolor: "#6d28d9", boxShadow: "none" },
            }}
          >
            Activate Module
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </OnsiteReuseContainer>
  );
};

export default SharableOnsite;
